import { createContext, useContext, useEffect, useRef, useCallback, useMemo, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/useAuth';
import { getAccessibleLocationIds, hasAllLocationAccess } from '@/core/auth/permissions';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { apiClient } from '@/shared/services/api';
import { useCompany } from '@/entities/company/hooks/useCompanyData';

// Query-key roots that are NOT scoped to a single location — company/tenant
// level, auth/account, and global directories. Everything else is treated as
// location-scoped and dropped on a scope switch (see the effect below).
// Keep this conservative: a missing root here only costs a harmless refetch,
// whereas wrongly listing a location-scoped root would re-introduce the
// cross-location stale-data bug.
const PRESERVED_QUERY_ROOTS = new Set<string>([
  'company', 'companies',
  'user', 'users', 'me', 'auth',
  'person', 'persons',
  'locations',
  'plans',
  'support', 'tickets',
  'bank-directory',
]);

export interface TenantContextValue {
  /** The active company (tenant) — equals company_schema from the JWT */
  companyId: string;
  companyName: string;
  /** Hex color code chosen by the company for branding (e.g. '#8b5cf6') */
  companyBrandColor: string | undefined;
  /** Schema name used by the backend for this tenant */
  schemaName: string;
  /** Locations this user has access to. Empty array = access to all (company_admin) */
  accessibleLocationIds: string[];
  /** The currently selected/active location (synced from ActiveContext) */
  currentLocationId: string | null;
  /** Switch the active location. Only allowed within accessibleLocationIds (or any if array is empty) */
  setCurrentLocationId: (locationId: string) => void;
  /** True if user can see all tenant locations */
  isCompanyLevel: boolean;
  /** Check if user has access to a specific location */
  hasLocationAccess: (locationId: string) => boolean;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const { activeContext, setActiveContext } = useActiveContext();
  const { data: company } = useCompany({ enabled: isAuthenticated });
  const queryClient = useQueryClient();
  const prevScopeRef = useRef<string | null>(null);

  const companyId = user?.companyId ?? '';
  const schemaName = user?.companyId ?? '';
  const accessibleLocationIds = useMemo(() => getAccessibleLocationIds(user), [user]);
  const isCompanyLevel = isAuthenticated && hasAllLocationAccess(user);

  // Derive currentLocationId from activeContext
  const currentLocationId = activeContext?.type === 'location' ? activeContext.id : null;

  // Scope identity for the purge below. Keyed on the whole active context,
  // not on the location id: company scope has no location id, so keying on
  // the id alone left `prev` null for the entire company visit and the
  // company→location transition never purged anything.
  const scopeKey = activeContext ? `${activeContext.type}:${activeContext.id}` : null;

  // Keep apiClient in sync so all GET requests are filtered by the active location
  useEffect(() => {
    apiClient.setLocationId(currentLocationId);

    const prev = prevScopeRef.current;
    prevScopeRef.current = scopeKey;

    // On a real scope SWITCH (not the initial mount/set), drop every
    // location-scoped cached query so React Query refetches it for the new
    // scope. Query keys now carry the scope (see `useScopeKey`), so this is
    // the second line of defence rather than the only one — it also frees
    // the memory of the scope the operator just left.
    if (prev !== null && prev !== scopeKey) {
      queryClient.removeQueries({
        predicate: (query) => {
          const root = query.queryKey?.[0];
          return !(typeof root === 'string' && PRESERVED_QUERY_ROOTS.has(root));
        },
      });
    }
  }, [currentLocationId, scopeKey, queryClient]);

  const hasLocationAccess = useCallback((locationId: string): boolean => {
    if (isCompanyLevel) return true;
    return accessibleLocationIds.includes(locationId);
  }, [isCompanyLevel, accessibleLocationIds]);

  const setCurrentLocationId = useCallback((locationId: string) => {
    if (!hasLocationAccess(locationId)) {
      console.warn(`[TenantContext] Access denied to location: ${locationId}`);
      return;
    }
    setActiveContext({ type: 'location', id: locationId });
  }, [hasLocationAccess, setActiveContext]);

  const companyName = company?.name ?? companyId;
  const companyBrandColor = company?.brandColor;

  const value = useMemo<TenantContextValue>(() => ({
    companyId,
    companyName,
    companyBrandColor,
    schemaName,
    accessibleLocationIds,
    currentLocationId,
    setCurrentLocationId,
    isCompanyLevel,
    hasLocationAccess,
  }), [companyId, companyName, companyBrandColor, schemaName, accessibleLocationIds, currentLocationId, setCurrentLocationId, isCompanyLevel, hasLocationAccess]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
